import { pdfjs } from "react-pdf";
import {
  applyPromiseWithResolversPolyfill,
  PROMISE_WITH_RESOLVERS_POLYFILL_SOURCE,
} from "./promiseWithResolversPolyfill";
import { logger } from "./logger";

// Bundled by Vite: polyfill + pdf.worker, same origin as the app (no CDN).
import localPolyfilledWorkerUrl from "../workers/pdf.worker.polyfilled.ts?worker&url";
// Same pdfjs version as react-pdf — used for same-origin blob bootstrap fallback.
import localPdfWorkerAssetUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

let configured = false;
let blobWorkerUrl: string | undefined;
let usingMainThreadFallback = false;

function createBlobPolyfillWorkerSrc(workerModuleUrl: string): string {
  const bootstrap = `${PROMISE_WITH_RESOLVERS_POLYFILL_SOURCE}import ${JSON.stringify(workerModuleUrl)};
`;
  if (blobWorkerUrl) {
    URL.revokeObjectURL(blobWorkerUrl);
  }
  blobWorkerUrl = URL.createObjectURL(
    new Blob([bootstrap], { type: "text/javascript" }),
  );
  return blobWorkerUrl;
}

/**
 * Main-thread polyfill + local worker that also polyfills.
 * Prefer Vite-bundled polyfilled worker; fall back to blob+local asset, then CDN.
 */
export function configurePdfJsWorker(): void {
  applyPromiseWithResolversPolyfill();

  if (usingMainThreadFallback) {
    return;
  }

  if (configured && pdfjs.GlobalWorkerOptions.workerSrc) {
    return;
  }

  const cdnWorkerUrl = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  // Prefer Vite-bundled polyfilled worker (same-origin; polyfill runs inside worker).
  if (localPolyfilledWorkerUrl) {
    pdfjs.GlobalWorkerOptions.workerSrc = localPolyfilledWorkerUrl;
    configured = true;
    logger.info(
      "[PDF Worker] Using Vite-bundled polyfilled worker",
      localPolyfilledWorkerUrl,
    );
    return;
  }

  try {
    pdfjs.GlobalWorkerOptions.workerSrc = createBlobPolyfillWorkerSrc(
      localPdfWorkerAssetUrl,
    );
    configured = true;
    logger.info(
      "[PDF Worker] Using blob polyfill bootstrap + local pdf.worker asset",
    );
    return;
  } catch (error) {
    logger.error(
      "[PDF Worker] Blob+local failed; falling back to CDN worker URL",
      error,
    );
  }

  try {
    pdfjs.GlobalWorkerOptions.workerSrc = createBlobPolyfillWorkerSrc(
      cdnWorkerUrl,
    );
    configured = true;
    logger.info("[PDF Worker] Using blob polyfill bootstrap + CDN worker");
  } catch (error) {
    logger.error(
      "[PDF Worker] All polyfilled worker setups failed; raw CDN as last resort",
      error,
    );
    pdfjs.GlobalWorkerOptions.workerSrc = cdnWorkerUrl;
    configured = true;
  }
}

/**
 * Last-resort: run pdf.js work on the main thread via its "fake worker" path.
 * Requires workerSrc plus globalThis.pdfjsWorker (see pdfjs PDFWorker setup).
 */
export async function configurePdfJsMainThreadFallback(): Promise<void> {
  applyPromiseWithResolversPolyfill();
  try {
    pdfjs.GlobalWorkerOptions.workerSrc =
      localPolyfilledWorkerUrl || localPdfWorkerAssetUrl;
    const workerModule = await import("pdfjs-dist/build/pdf.worker.min.mjs");
    (
      globalThis as typeof globalThis & {
        pdfjsWorker?: typeof workerModule;
      }
    ).pdfjsWorker = workerModule;
    usingMainThreadFallback = true;
    configured = true;
    logger.warn(
      "[PDF Worker] Switched to main-thread fake-worker fallback",
    );
  } catch (error) {
    usingMainThreadFallback = true;
    configured = true;
    logger.error("[PDF Worker] Main-thread fallback failed", error);
  }
}

export function hasConfiguredPdfJsMainThreadFallback(): boolean {
  return usingMainThreadFallback;
}
